/**
 * EVENTSFLO-CLIENT-CREDIT-UI-FORM-V1
 *
 * Self-contained Admin dialog to append a service credit line
 * onto a quote OR invoice, calling the existing backend endpoints:
 *   POST /api/admin/quotes/{id}/credit-lines
 *   POST /api/admin/invoices/{id}/credit-lines
 *
 * Hard UI rules:
 *  - No live calculation rewriting (the form only previews the
 *    multiplication crew × hours × rate; the backend recomputes
 *    totals).
 *  - Issued / accepted / approved quotes + issued / sent /
 *    overdue / partial invoices require the exact phrase
 *    `ADD SERVICE CREDIT TO ISSUED INVOICE`.
 *  - Paid / cancelled invoices block at the backend — we surface
 *    the blocker, no direct mutation.
 *  - Submit button disabled until required numeric fields are
 *    valid and the confirmation phrase (when needed) matches.
 *  - Zero edit/delete/payment/email controls.
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
// EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1
import { HelpPopover } from '../shared/HelpPopover';

export const CLIENT_CREDIT_REASONS = [
  { key: 'crew_late_arrival', label: 'Crew late arrival' },
  { key: 'incomplete_service', label: 'Incomplete service' },
  { key: 'early_crew_departure', label: 'Early crew departure' },
  { key: 'missing_crew_member', label: 'Missing crew member' },
  { key: 'reduced_scope_agreed_onsite', label: 'Reduced scope agreed onsite' },
  { key: 'client_goodwill_credit', label: 'Client goodwill credit' },
  { key: 'other', label: 'Other' },
];

export const CONFIRM_PHRASE = 'ADD SERVICE CREDIT TO ISSUED INVOICE';

const ISSUED_QUOTE_STATUSES = ['accepted', 'approved'];
const ISSUED_INVOICE_STATUSES = [
  'issued', 'sent', 'overdue', 'partial',
];
const PAID_BLOCKED_INVOICE_STATUSES = ['paid', 'cancelled'];

export default function AddServiceCreditDialog({
  open,
  onOpenChange,
  documentKind,   // 'quote' | 'invoice'
  documentId,
  documentStatus,
  currencySymbol = '€',
  onSuccess,
}) {
  const [reason, setReason] = useState('crew_late_arrival');
  const [crewCount, setCrewCount] = useState('');
  const [hoursCredited, setHoursCredited] = useState('');
  const [rateBasis, setRateBasis] = useState('');
  const [affectedRole, setAffectedRole] = useState('');
  const [clientVisibleNote, setClientVisibleNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  // EVENTSFLO-SERVICE-CREDIT-MIXED-VAT-AND-REGRESSION-TESTS-V1
  // Drives the Tax Rules Matrix evaluation for the credit line so
  // mixed-VAT documents apply the correct rate (not the header rate).
  const [creditTaxClass, setCreditTaxClass] = useState('manual_review');

  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const statusLc = (documentStatus || '').toString().toLowerCase();
  const isPaidBlocked = (
    documentKind === 'invoice'
    && PAID_BLOCKED_INVOICE_STATUSES.includes(statusLc)
  );
  const needsConfirmPhrase = (
    !isPaidBlocked
    && (
      (documentKind === 'quote'
       && ISSUED_QUOTE_STATUSES.includes(statusLc))
      || (documentKind === 'invoice'
       && ISSUED_INVOICE_STATUSES.includes(statusLc))
    )
  );

  const crew = Number(crewCount);
  const hrs = Number(hoursCredited);
  const rate = Number(rateBasis);
  const previewAmount = useMemo(() => {
    if (!crew || !hrs || !rate) return 0;
    return Math.round(crew * hrs * rate * 100) / 100;
  }, [crew, hrs, rate]);

  const numericValid = crew > 0 && hrs > 0 && rate > 0;
  const phraseOk = needsConfirmPhrase
    ? confirmPhrase === CONFIRM_PHRASE
    : true;
  const canSubmit = (
    !isPaidBlocked
    && numericValid
    && phraseOk
    && !submitResult
    && !submitting
  );

  const resetState = () => {
    setReason('crew_late_arrival');
    setCrewCount('');
    setHoursCredited('');
    setRateBasis('');
    setAffectedRole('');
    setClientVisibleNote('');
    setInternalNote('');
    setConfirmPhrase('');
    setCreditTaxClass('manual_review');
    setSubmitResult(null);
    setSubmitError(null);
  };

  const handleClose = (next) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitResult(null);
    try {
      const body = {
        reason,
        crew_count: crew,
        hours_credited: hrs,
        rate_basis: rate,
        affected_role: affectedRole || null,
        client_visible_note: clientVisibleNote || null,
        internal_note: internalNote || null,
        credit_tax_class: creditTaxClass || 'manual_review',
      };
      if (needsConfirmPhrase) body.confirm_phrase = confirmPhrase;
      const url = documentKind === 'invoice'
        ? `/admin/invoices/${encodeURIComponent(documentId)}/credit-lines`
        : `/admin/quotes/${encodeURIComponent(documentId)}/credit-lines`;
      const r = await api.post(url, body);
      setSubmitResult(r.data);
      toast.success('Service credit added.');
      if (onSuccess) onSuccess(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSubmitError(typeof detail === 'object'
        ? detail
        : { message: detail || err?.message || 'Request failed' });
      toast.error('Could not add service credit.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-xl"
        data-testid="add-service-credit-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="add-service-credit-title" className="flex items-center gap-2">
            Add service credit
            {/* EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1 */}
            <HelpPopover
              testId="service-credit-help"
              title="When to use a service credit"
            >
              <p>
                Use a service credit to compensate the client when a service
                was <strong>not fully provided</strong> (late crew, incomplete
                service, early departure, admin error, commercial gesture,
                etc.).
              </p>
              <p>
                A credit appears as a <strong>negative line</strong> on this
                {' '}{documentKind}. The subtotal is reduced by the credit
                amount before VAT is calculated.
              </p>
              <p>
                Credits are routed through the <strong>Tax Rules Matrix</strong>
                {' '}using the <strong>Credit Tax Category</strong> you choose
                below. On mixed-VAT documents (e.g. crew RC 0% + vehicle 20%)
                each credit gets the correct rate for what it actually
                relates to — not the document header rate.
              </p>
              <p>
                Never edit historical invoices to “remove” a service — always
                add a credit so the audit trail is preserved.
              </p>
            </HelpPopover>
          </DialogTitle>
          <DialogDescription>
            Append a negative line (Client Credit — Service Shortfall) to this {documentKind}.
            Existing positive line items are not modified.
          </DialogDescription>
        </DialogHeader>

        {isPaidBlocked && (
          <div
            className="rounded-md border border-rose-700/40 bg-rose-900/15 p-3 text-xs text-rose-200"
            data-testid="add-service-credit-paid-blocker"
          >
            This invoice is {statusLc}. Direct credit-line mutation is
            blocked. Use the credit-note workflow instead.
          </div>
        )}

        {!isPaidBlocked && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Reason</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="add-service-credit-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_CREDIT_REASONS.map((r) => (
                      <SelectItem
                        key={r.key}
                        value={r.key}
                        data-testid={`add-service-credit-reason-${r.key}`}
                      >
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Affected role (optional)</Label>
                <Input
                  value={affectedRole}
                  onChange={(e) => setAffectedRole(e.target.value)}
                  placeholder="e.g. Lead Tech"
                  data-testid="add-service-credit-affected-role"
                />
              </div>
            </div>

            {/* EVENTSFLO-SERVICE-CREDIT-MIXED-VAT-AND-REGRESSION-TESTS-V1 */}
            <div>
              <Label className="text-xs flex items-center gap-1">
                Credit Tax Category / Applies To
                <HelpPopover
                  testId="service-credit-tax-class-help"
                  title="Why the Credit Tax Category matters"
                >
                  <p>
                    Choose what the credit relates to. This drives which
                    Tax Rules Matrix row is used to calculate VAT on the
                    credit line.
                  </p>
                  <p>
                    <strong>Crew / Service</strong> on a UK/EU reverse-charge
                    client will use <strong>VAT 0% RC</strong>.
                    <br />
                    <strong>Equipment / Vehicle Rental</strong> stays
                    <strong>TVA-taxable</strong> at the matrix rate.
                    <br />
                    <strong>General / Manual Review</strong> flags the
                    credit for accountant review.
                  </p>
                  <p>If unsure, keep the default (General / Manual Review).</p>
                </HelpPopover>
              </Label>
              <Select value={creditTaxClass} onValueChange={setCreditTaxClass}>
                <SelectTrigger data-testid="service-credit-tax-class-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crew_service" data-testid="service-credit-tax-class-crew_service">
                    Crew / Service
                  </SelectItem>
                  <SelectItem value="crew_allowance" data-testid="service-credit-tax-class-crew_allowance">
                    Crew Allowance / Meal
                  </SelectItem>
                  <SelectItem value="crew_chief_service" data-testid="service-credit-tax-class-crew_chief_service">
                    Crew Chief
                  </SelectItem>
                  <SelectItem value="crew_service_supplement" data-testid="service-credit-tax-class-crew_service_supplement">
                    Crew Service Supplement
                  </SelectItem>
                  <SelectItem value="equipment_rental" data-testid="service-credit-tax-class-equipment_rental">
                    Equipment Rental
                  </SelectItem>
                  <SelectItem value="vehicle_rental" data-testid="service-credit-tax-class-vehicle_rental">
                    Vehicle Rental
                  </SelectItem>
                  <SelectItem value="travel_cost" data-testid="service-credit-tax-class-travel_cost">
                    Travel Cost
                  </SelectItem>
                  <SelectItem value="accommodation" data-testid="service-credit-tax-class-accommodation">
                    Accommodation
                  </SelectItem>
                  <SelectItem value="manual_review" data-testid="service-credit-tax-class-manual_review">
                    General / Manual Review
                  </SelectItem>
                </SelectContent>
              </Select>
              {creditTaxClass === 'manual_review' && (
                <div
                  className="mt-1 text-[11px] text-amber-500"
                  data-testid="service-credit-manual-review-warning"
                >
                  This credit will be flagged for manual review. An accountant
                  should verify the applied VAT rate before issuing.
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Crew count</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={crewCount}
                  onChange={(e) => setCrewCount(e.target.value)}
                  data-testid="add-service-credit-crew-count"
                />
              </div>
              <div>
                <Label className="text-xs">Hours credited</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  value={hoursCredited}
                  onChange={(e) => setHoursCredited(e.target.value)}
                  data-testid="add-service-credit-hours"
                />
              </div>
              <div>
                <Label className="text-xs">Rate basis ({currencySymbol})</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rateBasis}
                  onChange={(e) => setRateBasis(e.target.value)}
                  data-testid="add-service-credit-rate"
                />
              </div>
            </div>

            <div
              className="rounded-md border border-zinc-800 p-2 bg-zinc-900/40 text-xs"
              data-testid="add-service-credit-preview"
            >
              <span className="text-muted-foreground">Preview: </span>
              {crew || 0} × {hrs || 0} × {currencySymbol}{rate || 0}{' '}
              ={' '}
              <span
                className="font-mono text-rose-300"
                data-testid="add-service-credit-preview-amount"
              >
                -{currencySymbol}{previewAmount.toFixed(2)}
              </span>
              <span className="text-muted-foreground ml-2">
                (credit appears as a negative line)
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs">Client-visible note (optional)</Label>
                <Textarea
                  rows={2}
                  value={clientVisibleNote}
                  onChange={(e) => setClientVisibleNote(e.target.value)}
                  placeholder="Will appear on the PDF"
                  data-testid="add-service-credit-client-note"
                />
              </div>
              <div>
                <Label className="text-xs">Internal note (optional)</Label>
                <Textarea
                  rows={2}
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Admin-only"
                  data-testid="add-service-credit-internal-note"
                />
              </div>
            </div>

            {needsConfirmPhrase && (
              <div className="rounded-md border border-amber-700/40 bg-amber-900/15 p-3 space-y-2">
                <Label
                  className="text-xs text-amber-200"
                  data-testid="add-service-credit-confirm-label"
                >
                  This {documentKind} is {statusLc}. Type exactly:{' '}
                  <code className="text-[10px]">{CONFIRM_PHRASE}</code>
                </Label>
                <Input
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  data-testid="add-service-credit-confirm-phrase"
                />
              </div>
            )}
          </div>
        )}

        {submitError && (
          <div
            className="rounded-md border border-rose-700/40 bg-rose-900/10 p-3 text-xs text-rose-200 space-y-1"
            data-testid="add-service-credit-error"
          >
            {submitError.blocker && (
              <div>
                <Badge variant="outline" className="mr-2">
                  {submitError.blocker}
                </Badge>
                {submitError.required_phrase && (
                  <code className="text-[10px]">
                    {submitError.required_phrase}
                  </code>
                )}
              </div>
            )}
            {submitError.message && <div>{submitError.message}</div>}
            {Array.isArray(submitError.allowed_reasons) && (
              <div className="text-[10px] text-muted-foreground">
                Allowed reasons: {submitError.allowed_reasons.join(', ')}
              </div>
            )}
          </div>
        )}

        {submitResult && (
          <div
            className="rounded-md border border-emerald-700/40 bg-emerald-900/10 p-3 text-xs text-emerald-200 space-y-1"
            data-testid="add-service-credit-success"
          >
            <div className="text-sm font-medium text-emerald-300">
              Service credit added
            </div>
            <div>
              <span className="text-muted-foreground">Credit line total: </span>
              <span
                className="font-mono"
                data-testid="add-service-credit-success-credit"
              >
                {currencySymbol}{Number(submitResult.credit_line_total || 0).toFixed(2)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">New total payable: </span>
              <span
                className="font-mono"
                data-testid="add-service-credit-success-total"
              >
                {currencySymbol}{Number(submitResult.total_payable || 0).toFixed(2)}
              </span>
            </div>
            <div className="pt-1">
              <a
                href={documentKind === 'invoice'
                  ? `/api/invoices/${encodeURIComponent(documentId)}/pdf?view=summary`
                  : `/api/quotes/${encodeURIComponent(documentId)}/pdf?view=summary`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-300 underline"
                data-testid="add-service-credit-pdf-preview-link"
              >
                Open PDF preview →
              </a>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            data-testid="add-service-credit-cancel-btn"
          >
            {submitResult ? 'Close' : 'Cancel'}
          </Button>
          {!submitResult && !isPaidBlocked && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="add-service-credit-submit-btn"
            >
              {submitting ? 'Adding…' : 'Add service credit'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Compact read-only credit history table — same file for locality
// ---------------------------------------------------------------------------
export function CreditHistoryTable({ lines, currencySymbol = '€' }) {
  const creditLines = (lines || []).filter(
    (li) => (li.line_type === 'credit')
      || (typeof li.line_total === 'number' && li.line_total < 0),
  );
  if (creditLines.length === 0) return null;
  return (
    <div
      className="border border-zinc-800 rounded-md p-3"
      data-testid="client-credit-history"
    >
      <div className="text-xs font-medium mb-1">
        Client credits ({creditLines.length})
      </div>
      <table className="w-full text-xs" data-testid="client-credit-history-table">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th>Reason / description</th>
            <th>Crew</th>
            <th>Hours</th>
            <th>Rate</th>
            <th>Credit</th>
            <th>Client note</th>
          </tr>
        </thead>
        <tbody>
          {creditLines.map((li, i) => (
            <tr
              key={li.id || i}
              data-testid={`client-credit-history-row-${i}`}
              className="border-t border-zinc-800 align-top"
            >
              <td className="pr-2 py-1">{li.description || '—'}</td>
              <td>{li.credit_meta?.crew_count ?? '—'}</td>
              <td>{li.credit_meta?.hours_credited ?? '—'}</td>
              <td>
                {li.credit_meta?.rate_basis
                  ? `${currencySymbol}${Number(li.credit_meta.rate_basis).toFixed(2)}`
                  : '—'}
              </td>
              <td className="font-mono text-rose-300">
                {currencySymbol}{Number(li.line_total || 0).toFixed(2)}
              </td>
              <td className="text-muted-foreground">
                {li.credit_meta?.client_visible_note || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
